import { Handle, NodeProps, Position, useReactFlow } from 'reactflow';
import { ReactSVG } from 'react-svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { operationType } from '../../services/types';
import { Input } from './children/functions/input';
import { OpenAI } from './children/LLMs/openai';
import { Deepseek } from './children/LLMs/deepseek';
import { Qwen } from './children/LLMs/qwen';
import { DragifyAI } from './children/LLMs/dragifyAI';
import { DocSearch } from './children/knowledge/upload/docSearch';
import { FileUploader } from './children/knowledge/upload/files';
import { Spreadsheet } from './children/loaders/spreadsheet';
import { GoogleGemini } from './children/LLMs/google';
import { Gmail } from './children/actions/gmail';
import { Outlook } from './children/actions/outlook';
import { VideoVision } from './children/vision/videoVision';
import { OCR } from './children/vision/ocr';
import { UrlSearch } from './children/knowledge/urlSearch';
import { GoogleDrive } from './children/knowledge/googleDrive';
import { AzureStorage } from './children/knowledge/azureStorage';
import { Main } from './style';
import { Jira } from './children/actions/jira';
import { BigQuery } from './children/loaders/bigQuery';
import { Odoo } from './children/crm/odoo';
import { ClickUp } from './children/actions/clickup';
import { FlowQueue } from './children/functions/flowQueue'; 
import { Trello } from './children/tools/trello';
import { WordPress } from './children/cms/wordpress';
import { Slack } from './children/tools/slack';
import { Teams } from './children/tools/teams';
import { SharePoint } from './children/tools/sharepoint';
import { CustomAPI } from './children/actions/customAPI';
import { PDFChunker } from './children/knowledge/pdfChunker';
import { KnowledgeBase } from './children/knowledge/knowledgeBase';
import { OneDrive } from './children/tools/oneDrive';
import { OneDriveTrigger } from './children/triggers/oneDriveTrigger';
import { Webhook } from './children/knowledge/webhook';
import { Sequencer } from './children/agents/sequencer';
import { QueryDatabase } from './children/knowledge/queryDatabase';
import { Routing } from './children/functions/routing';
import { JevClassifier } from './children/functions/jevClassifier';
import { ActionsAgent } from './children/agents/actionsAgent';
import { ChatAgent } from './children/agents/chatAgent';
import { Code } from './children/functions/code';
import { AiAgent } from './children/agents/aiAgent';
import { Minimize, Settings, Trash, X, MessagesSquare, AlertTriangle } from 'lucide-react';
import { Llama } from './children/LLMs/llama';
import { handleRemoveNode, handleRemoveVariablesFromBlock } from '../../services/method';
import { useSearchParams } from 'react-router-dom';
import { Start } from './children/actions/start';
import { WebSearch } from './children/knowledge/webSearch';
import { BrowserUse } from './children/knowledge/browserUse';
import { ComputerUse } from './children/knowledge/computerUse';
import { SubWorkflow } from './children/knowledge/subWorkflow';
import { SlackTrigger } from './children/triggers/slackTrigger';
import { TeamsTrigger } from './children/triggers/teamsTrigger';
import { SharePointTrigger } from './children/triggers/sharepointTrigger';
import { useDispatch, useSelector } from 'react-redux';
import { setProjectData } from '../../redux/reducers/project';
import { GoogleCalendarTrigger } from './children/triggers/googleCalendarTrigger';
import { GoogleCalendar } from './children/actions/googleCalendar';
import { GoogleDriveTrigger } from './children/triggers/googleDriveTrigger';
import { MessengerSendMessage } from './children/actions/messengerSend';
import { InstagramSendMessage } from './children/actions/instagramSend';
import { MetaListenerTrigger } from './children/triggers/metaListenerTrigger';
import { RssNode } from './children/actions/rss';
import { EditFields } from './children/data/editFields';
import { TypeformTrigger } from './children/triggers/typeformTrigger';
import { TextToSpeech } from './children/vision/textToSpeech';
import { SpeechToText } from './children/vision/speechToText';
import { MergeBlock } from './children/data/merge';
import { WaitBlock } from './children/functions/wait';
import { Sql } from './children/actions/sql';
import { SqlTrigger } from './children/triggers/sqlTrigger';
import { ERPNext } from './children/crm/erpnext';
import { GoogleFormTrigger } from './children/triggers/googleFormTrigger';
import { WhatsappSendMessage } from './children/actions/whatsappSendMessage';
import { WhatsappListener } from './children/triggers/whatsappListener';
import { WhatsAppCloudTrigger } from './children/triggers/whatsappCloudTrigger';
import { WhatsAppCloudSendMessage } from './children/actions/whatsappCloudSendMessage';
import { Notion } from './children/crm/notion';
import { OutlookTrigger } from './children/triggers/outlookTrigger';
import { TwilioSendSms } from './children/actions/twilioSendSms';
import { Airtable } from './children/databases/airtable';
import { Supabase } from './children/databases/supabase';
import { DataTable } from './children/databases/dataTable';
import { ImapTrigger } from './children/triggers/imapTrigger';
import { FacebookPosting } from './children/actions/facebookPosting';
import { InstagramPosting } from './children/actions/instagramPosting';
import { FilterBlock } from './children/flowControl/filter';
import { Shopify } from './children/actions/shopify';
import { RootState } from '../../redux/store';
import { ShopifyTrigger } from './children/triggers/shopifyTrigger';
import { OdooTrigger } from './children/triggers/odooTrigger';
import { WooCommerceTrigger } from './children/triggers/woocommerceTrigger';
import { WooCommerce } from './children/actions/woocommerce';
import { EasyOrders } from './children/actions/easyOrders';
import { EasyOrdersTrigger } from './children/triggers/easyOrdersTrigger';
import { Bosta } from './children/actions/bosta';
import { BostaTrigger } from './children/triggers/bostaTrigger';
import { MediaIntelligence } from './children/vision/mediaIntelligence';
import { DocumentIntelligence } from './children/vision/documentIntelligence';
import { theme } from '../../styles/theme';

export function CustomNode(props: NodeProps<operationType>) {
   const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
   const dispatch = useDispatch();
   const isThereBlockExpanded = useSelector((state: RootState) => state.project.isThereBlockExpanded);
   const [searchParams] = useSearchParams();
   const projectId = searchParams.get('id');
   const userId = useSelector((state: RootState) => state.user.data?._id);

   // ---
   const handleMinimize = () => {
      if (typeof props.data.childOnMinimize === "function") {
         props.data.childOnMinimize();
         dispatch(setProjectData({ isThereBlockExpanded: true }));

      }
   }
   const handleSettings = () => {
      if (typeof props.data.childOnSettings === "function") {
         props.data.childOnSettings();
      }
   }
   const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleRemoveNode({ id: props.id, action: props.data.action, index: props.data.index, projectId, userId, getNodes, getEdges, setNodes, setEdges });
   }

   // ---
   const renderContent = () => {
      const Component = {
         input: Input,
         output: Input,
         openai: OpenAI,
         deepseek: Deepseek,
         qwen: Qwen,
         dragifyAI: DragifyAI,
         googleGemini: GoogleGemini,
         docSearch: DocSearch,
         fileUploader: FileUploader,
         spreadsheet: Spreadsheet,
         gmail: Gmail,
         outlook: Outlook,
         videoVision: VideoVision,
         ocr: OCR,
         urlSearch: UrlSearch,
         googleDrive: GoogleDrive,
         azureStorage: AzureStorage,
         jira: Jira,
         bigQuery: BigQuery,
         odoo: Odoo,
         clickup: ClickUp,
         flowQueue: FlowQueue,
         trello: Trello,
         wordPress: WordPress,
         slack: Slack,
         teams: Teams,
         sharepoint: SharePoint,
         customAPI: CustomAPI,
         pdfChunker: PDFChunker,
         knowledge: KnowledgeBase,
         oneDrive: OneDrive,
         webhook: Webhook,
         sequencer: Sequencer,
         queryDatabase: QueryDatabase,
         routing: Routing,
         jevClassifier: JevClassifier,
         actionsAgent: ActionsAgent,
         chatAgent: ChatAgent,
         code: Code,
         aiAgent: AiAgent,
         llama: Llama,
         start: Start,
         webSearch: WebSearch,
         browserUse: BrowserUse,
         computerUse: ComputerUse,
         subWorkflow: SubWorkflow,
         slackTrigger: SlackTrigger,
         teamsTrigger: TeamsTrigger,
         sharepointTrigger: SharePointTrigger,
         googleCalendar: GoogleCalendar,
         googleCalendarTrigger: GoogleCalendarTrigger,
         googleDriveTrigger: GoogleDriveTrigger,
         oneDriveTrigger: OneDriveTrigger,
         messengerSendMessage: MessengerSendMessage,
         instaSendMessage: InstagramSendMessage,
         metaListenerTrigger: MetaListenerTrigger,
         rss: RssNode,
         editFields: EditFields,
         typeformTrigger: TypeformTrigger,
         textToSpeech: TextToSpeech,
         speechToText: SpeechToText,
         merge: MergeBlock,
         wait: WaitBlock,
         sql: Sql,
         sqlTrigger: SqlTrigger,
         erpnext: ERPNext,
         googleFormTrigger: GoogleFormTrigger,
         whatsappListener: WhatsappListener,
         whatsappSendMessage: WhatsappSendMessage,
         whatsappCloudTrigger: WhatsAppCloudTrigger,
         whatsappCloudSendMessage: WhatsAppCloudSendMessage,
         notion: Notion,
         outlookTrigger: OutlookTrigger,
         twilioSendSms: TwilioSendSms,
         airtable: Airtable,
         supabase: Supabase,
         dataTable: DataTable,
         imapTrigger: ImapTrigger,
         facebookPosting: FacebookPosting,
         instagramPosting: InstagramPosting,
         shopify: Shopify,
         filter: FilterBlock,
         shopifyTrigger: ShopifyTrigger,
         odooTrigger: OdooTrigger,
         easyOrdersTrigger: EasyOrdersTrigger,
         bostaTrigger: BostaTrigger,
         woocommerceTrigger: WooCommerceTrigger,
         woocommerce: WooCommerce,
         easyOrders: EasyOrders,
         bosta: Bosta,
         mediaIntelligence: MediaIntelligence,
         documentIntelligence: DocumentIntelligence
      }[props.data.action];
      return Component ? 
         <Component { ...props } 
            setChildMinimize={ (fn: any) => (props.data.childOnMinimize = fn) } 
            setChildSettings={(fn: any) => (props.data.childOnSettings = fn)} 
         /> 
      : null;
   };

   const isMain = props.data.action === 'input' || props.data.action === 'output';
   const isExpanded = props.data.action === 'ocr' && props.data.value?.isNoMinimize;
   const isMinimizeCard = (!isMain && props.data.action !== 'ocr') || (props.data.action === 'ocr' && !props.data.value?.isNoMinimize);

   const validationIssueSeverity = (props.data as any)?.validationIssueSeverity as 'error' | 'warning' | undefined;
   const issueFocusHighlight = (props.data as any)?.issueFocusHighlight as 'error' | 'warning' | undefined;
   const normalizedPostions: Array<{ type: 'source' | 'target'; label: string; dir: Position }> = 
      props.data.action === 'aiAgent' ? [
         { type: 'target', label: 'Input', dir: Position.Left },
         { type: 'source', label: 'Output', dir: Position.Right },
         { type: 'source', label: 'Tools', dir: Position.Right },
      ]
      : props.data.postions.map((postion) => ({
         type: postion.type as 'source' | 'target',
         label: postion.label || '',
         dir: postion.dir,
      }));

   const mainClassName = [
      props.data.action,
      props.data.executionError ? 'execution-error' : '',
      issueFocusHighlight ? `issue-focus issue-focus-${issueFocusHighlight}` : '',
   ].filter(Boolean).join(' ');

   return (<>
      <Main
         className={ mainClassName }
         $isMinimize={ isMinimizeCard }
         title={ isMinimizeCard ? props.data.name : undefined }
         onDoubleClick={ _=> {
            if (!isThereBlockExpanded) handleMinimize();
         }}
      >
         {validationIssueSeverity ? (
            <div
               className={`validationIssueBadge ${validationIssueSeverity}`}
               title={validationIssueSeverity === 'error' ? 'Critical issue in this node' : 'Performance issue in this node'}
            >
               <AlertTriangle size={10} strokeWidth={2.1} />
            </div>
         ) : null}
         <div className="mainTitle">
            <div style={{ background: props.data.background || theme.colors.primary }}>{ 
               typeof props.data.icon === 'string' ? 
               (props.data.icon.endsWith('.png') || props.data.icon.endsWith('.jpg') 
                  ? <img src={props.data.icon} alt={props.data.name || ''} title={props.data.name} style={{ width: isMinimizeCard ? 24 : 32, height: isMinimizeCard ? 24 : 32, objectFit: 'contain' }} /> 
                  : <ReactSVG className="otherSvg" src={props.data.icon} />) 
                  : <FontAwesomeIcon icon={props.data.icon} />
            }</div>
            { isMinimizeCard ? null : isMain || isExpanded ? (
               <div className="nMeta">
                  <h5>{ props.data.name }</h5>
                  <div>
                     <span className="index">
                        <span style={{ backgroundColor: props.data.background }}></span>
                        <span style={{ color: props.data.background }}>{ props.data.index }</span>
                     </span>
                     <span 
                        className="remove" 
                        onClick={ _=> handleRemoveVariablesFromBlock({ id: props.id, getNodes, getEdges, setNodes, setEdges }) }
                     >
                        <X size={ 20 } />
                     </span>
                  </div>
               </div>
            ) : null }
            { isMinimizeCard && !isMain ? (
               <div className="nodeMid" aria-hidden="true">
                  <div className="nodeDivider" />
               </div>
            ) : null }
            { !isMain ? <div style={{ flex: '0 0 auto' , width : 'fit-content' , marginLeft : -10}} className={`  settings nodrag nopan${props.data.action === 'aiAgent' ? ' compact' : ''}`}>
               <span onClick={ handleMinimize } title="Expand"><Minimize /></span>
               { props.data.action === 'aiAgent' ? (
                  <span onClick={ handleSettings } title="Open Chat">
                     <MessagesSquare />
                  </span>
               ) : null }
               <span className="del" onClick={ handleDelete } title="Delete"><Trash /></span>
            </div> : null }
         </div>
         <div 
            className="content" 
            onDoubleClick={ e => {
               if (isThereBlockExpanded) e.stopPropagation()
            }}
         >
            { renderContent() }
         </div>
      </Main>
      { normalizedPostions.map((postion, index) => (
         <Handle 
            key={ index.toString() } 
            id={ postion.label } 
            type={ postion.type } 
            position={ postion.dir } 
            className={`handle-${props.data.action} handle-child-${postion.label}`}
         >
            { postion.label ? <span className="handleTitle">{ postion.label }</span> : null }
         </Handle>
      ))}
   </>);
}
